"use client";

import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  contactSchema,
  ContactFormData,
  PROJECT_TYPES,
  REFERRAL_SOURCES,
} from "@/lib/contact-schema";
import type { z } from "zod";
import SelectInput from "@/components/form/Select/Select";
import styles from "./ContactForm.module.scss";

// Input type allows `website` to be omitted (it has a .default(""))
type ContactFormInput = z.input<typeof contactSchema>;

const inputClass =
  "rounded-button border border-border-color w-full h-[70px] px-10 leading-none text-black bg-transparent focus:outline-none focus:ring-1 focus:ring-[#197875] transition-colors";

const textareaClass =
  "rounded-button border border-border-color w-full px-10 py-8 leading-relaxed text-black bg-transparent focus:outline-none focus:ring-1 focus:ring-[#197875] transition-colors resize-none";

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormInput, unknown, ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { website: "" },
  });

  const onSubmit = async (data: ContactFormData) => {
    setServerError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
    } catch {
      setServerError(
        "Something went wrong — please try again, or email me directly at paul@paullackner.studio"
      );
    }
  };

  if (submitted) {
    return (
      <div className={styles.success}>
        <p className={styles["success-heading"]}>Thanks — I'll be in touch.</p>
        <p className={styles["success-body"]}>
          I typically respond within one business day.
        </p>
      </div>
    );
  }

  const projectTypeOptions = PROJECT_TYPES.map((v) => ({ value: v, label: v }));
  const referralOptions = REFERRAL_SOURCES.map((v) => ({ value: v, label: v }));

  return (
    <form
      className={styles["contact-form"]}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      {/* Honeypot — hidden from real users, visible to bots */}
      <input
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        className={styles.honeypot}
        {...register("website")}
      />

      {/* Row 1: Name + Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className={styles.field}>
          <input
            type="text"
            placeholder="Your name"
            autoComplete="name"
            className={inputClass}
            aria-label="Your name"
            {...register("name")}
          />
          {errors.name && (
            <span className={styles.error}>{errors.name.message}</span>
          )}
        </div>
        <div className={styles.field}>
          <input
            type="email"
            placeholder="Your email"
            autoComplete="email"
            className={inputClass}
            aria-label="Your email"
            {...register("email")}
          />
          {errors.email && (
            <span className={styles.error}>{errors.email.message}</span>
          )}
        </div>
      </div>

      {/* Row 2: Organization + Project Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className={styles.field}>
          <input
            type="text"
            placeholder="Organization or company"
            autoComplete="organization"
            className={inputClass}
            aria-label="Organization or company"
            {...register("organization")}
          />
          {errors.organization && (
            <span className={styles.error}>{errors.organization.message}</span>
          )}
        </div>
        <div className={styles.field}>
          <Controller
            name="projectType"
            control={control}
            render={({ field }) => (
              <SelectInput
                options={projectTypeOptions}
                placeholder="What are you working on?"
                value={field.value ?? ""}
                onChange={(val: string) => field.onChange(val)}
                name="projectType"
              />
            )}
          />
          {errors.projectType && (
            <span className={styles.error}>{errors.projectType.message}</span>
          )}
        </div>
      </div>

      {/* Row 3: Message */}
      <div className={`${styles.field} mb-4`}>
        <textarea
          rows={5}
          placeholder="A sentence or two is plenty — what are you working on, and what's the timeline?"
          className={textareaClass}
          aria-label="Tell me about your project"
          {...register("message")}
        />
        {errors.message && (
          <span className={styles.error}>{errors.message.message}</span>
        )}
      </div>

      {/* Row 4: Referral (half-width) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className={styles.field}>
          <Controller
            name="referral"
            control={control}
            render={({ field }) => (
              <SelectInput
                options={referralOptions}
                placeholder="How did you find me?"
                value={field.value ?? ""}
                onChange={(val: string) => field.onChange(val)}
                name="referral"
              />
            )}
          />
          {errors.referral && (
            <span className={styles.error}>{errors.referral.message}</span>
          )}
        </div>
      </div>

      {/* Submit button */}
      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-button bg-foreground text-white h-[70px] px-10 hover:bg-[#197875] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
        >
          {isSubmitting ? "Sending…" : "Send message"}
        </button>
      </div>

      {serverError && (
        <p className={styles["form-error"]}>{serverError}</p>
      )}
    </form>
  );
}
